<template>
  <div v-show="isShow" class="popup_wrap" @click.stop>
    <div class="popup_box">
      <div class="guide" v-html="$tc('lineqr_01_guide')"></div>
      <div class="qr_wrap">
        <div class="qr_box">
          <img v-if="qrSrc" :src="qrSrc" />
        </div>
        <div class="count_down">{{ displayTime }}</div>
      </div>
      <button class="btn_cancel" @click="cancel">{{ $tc('waiting_cancel') }}</button>
    </div>
  </div>
</template>
<script>
import QRCode from 'qrcode'
import { createLineTempKey } from '@/api/waiting'

const WAITING_SEC = 180 // QR 유효 시간(초) — 만료 시 timeout 이벤트 발생

// 키오스크 locale -> LIFF 페이지 언어 코드 매핑
const LIFF_LANG_MAP = { ko: 'ko', en: 'en', jp: 'ja', cn_simplified: 'zh' }

export default {
  name: 'line-qr-popup',
  props: {
    isShow: { type: Boolean }
  },
  data () {
    return {
      tempKey: '',
      qrSrc: '',
      remainSec: WAITING_SEC,
      expireAt: 0, // 카운트다운 만료 시각 (0이면 타이머 비활성)
      intervalObj: null
    }
  },
  computed: {
    displayTime () {
      const min = Math.floor(this.remainSec / 60)
      const sec = this.remainSec % 60
      return `${min}:${sec >= 10 ? sec : '0' + sec}`
    },
    receivedLineTempKey () {
      return this.$store.state.receivedLineTempKey
    }
  },
  watch: {
    isShow (val) {
      if (val) {
        this.open()
      } else {
        this.clear()
      }
    },
    // 소켓으로 수신한 임시키가 이 기기에서 발급한 키와 일치할 때만 인증 완료 처리
    receivedLineTempKey (val) {
      if (!this.isShow || !val || val !== this.tempKey) return
      this.clear()
      this.$store.commit('setWaitingParams', { tempKey: this.tempKey })
      this.$emit('success')
    }
  },
  methods: {
    open () {
      this.tempKey = ''
      this.qrSrc = ''
      this.$store.commit('mutationByKeyValue', [{ key: 'receivedLineTempKey', value: '' }])
      const restaurantIdx = this.$store.getters.getStoreIdx
      createLineTempKey(restaurantIdx).then(({ data }) => {
        this.tempKey = data.tempKey
        // 키오스크에서 선택한 언어를 LIFF 페이지에 전달
        const lang = LIFF_LANG_MAP[this.$store.state.locale] || 'en'
        const liffUrl = `${data.liffUrl}${data.liffUrl.includes('?') ? '&' : '?'}lang=${lang}`
        return QRCode.toDataURL(liffUrl, { width: 500, margin: 1 })
      }).then((src) => {
        this.qrSrc = src
        this.startTimer()
      }).catch((error) => {
        console.error('LINE temp-key 발급 실패---', error)
        this.$emit('error')
      })
    },
    startTimer () {
      this.stopTimer()
      // 화면이 백그라운드로 가면 setInterval이 지연/정지되어 카운트다운이 실제 시간보다 늦게 흐른다.
      // 만료 시각을 고정해두고 매 틱마다 실제 경과 시간으로 재계산해 서버의 임시키 TTL과 어긋나지 않게 한다.
      this.expireAt = Date.now() + WAITING_SEC * 1000
      this.syncRemainSec()
      this.intervalObj = setInterval(this.syncRemainSec, 1000)
      // 백그라운드에서 복귀한 순간 다음 틱을 기다리지 않고 즉시 만료 여부를 반영한다
      document.addEventListener('visibilitychange', this.syncRemainSec)
    },
    syncRemainSec () {
      if (!this.expireAt) return // 타이머 종료 후 잔여 호출로 인한 중복 timeout 방지
      this.remainSec = Math.max(0, Math.ceil((this.expireAt - Date.now()) / 1000))
      if (this.remainSec <= 0) {
        this.stopTimer()
        this.$emit('timeout')
      }
    },
    stopTimer () {
      clearInterval(this.intervalObj)
      document.removeEventListener('visibilitychange', this.syncRemainSec)
      this.expireAt = 0
    },
    clear () {
      this.stopTimer()
      this.$store.commit('mutationByKeyValue', [{ key: 'receivedLineTempKey', value: '' }])
    },
    cancel () {
      this.clear()
      this.$emit('close')
    }
  },
  destroyed () {
    this.stopTimer()
  }
}
</script>
<style scoped lang="scss">
@import "@/assets/base.scss";

.popup_wrap {
  position: fixed;
  left: 0;
  top: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100vw;
  height: 100vh;
  z-index: 900;
  background: rgba(0, 0, 0, 0.6);
}

.popup_box {
  display: flex;
  flex-direction: column;
  @include flex-gap(5vh, column);
  width: 66.40625vw;
  max-height: calc(100vh - 60px);
  padding: 7.5vh 3.90625vw 6.25vh;
  border-radius: 4vh;
  border: 1px solid $e0;
  background: $white;
  overflow-y: auto;
  box-sizing: border-box;
}

.guide {
  color: $black;
  text-align: center;
  font-size: 32px; // IE11 fallback
  font-size: unquote("min(2.5vw, 32px)");
  font-weight: 700;
  line-height: 6vh;
}

.qr_wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  @include flex-gap(2vh, column);
  .qr_box {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 35vh;
    height: 35vh;
    border-radius: 3vh;
    border: 1px solid $gray_200;
    background: $white;
    img {
      width: 31.25vh;
      height: 31.25vh;
    }
  }
  .count_down {
    color: #D81B1B;
    text-align: center;
    font-size: 24px; // IE11 fallback
    font-size: unquote("min(1.875vw, 24px)");
    font-weight: 700;
    line-height: 1.2;
  }
}

.btn_cancel {
  flex-shrink: 0;
  width: 100%;
  height: 11.75vh;
  padding: 3.25vh 0.78125vw;
  justify-content: center;
  align-items: center;
  border-radius: 2.5vh;
  background: #E6E8EB;
  color: #2E3137;
  font-size: 32px; // IE11 fallback
  font-size: unquote("min(2.5vw, 32px)");
  font-weight: 700;
  line-height: 4.75vh;
}
</style>
